import React, { Suspense, useContext, useEffect } from 'react';

import classname from '@search/classname/src';
import { errorCatcherWrap } from '@search/error/src/catcher';
import { useGql } from '@search/gql-client/src/useGql';
import { INewBuildingCardType } from '@search/nb-routes/src/NewBuildingCardRoute';
import {
    ImageLazySrcSetProvide,
    useClImageLazySrcSetUrl
} from '@search/vtbeco-frontend-core/domain/image/lazy/ImageLazySrcSet';

import { Shared } from '../../../../types/shared';

import { GlobalContext, IOnChangeGlobalContext, OnChangeGlobalContext } from '../../../common/components/GlobalContext';
import Page from '../../../common/components/Page';
import ErrorBoundary from '../../../common/components/ErrorBoundary';
import { GalleryContextProvider } from '../../../common/components/Gallery/GalleryContext';
import { FSGallery } from '../../../common/components/Gallery/FSGallery';
import { query } from '../../../common/components/NewBuildingConstruction';
import type {
    NewBuildingConstructionQuery$data as NewBuildingConstructionQueryResponse,
    NewBuildingConstructionQuery$variables as NewBuildingConstructionQueryVariables
} from '../../../common/components/NewBuildingConstruction/__generated__/NewBuildingConstructionQuery.graphql';

import NewBuildingConstruction from '../NewBuildingConstruction';

import './styles.css';

const cn = classname.bind(null, 'NewBuildingConstructionPage');

const NewBuildingConstructionWrapCatcher = errorCatcherWrap(NewBuildingConstruction);

const NewBuildingConstructionPage = ({
    params
}: Shared.NewBuildingConstructionPageProps) => {
    const onChangeGlobalContext = useContext<IOnChangeGlobalContext>(OnChangeGlobalContext);
    const { instanceConfig: { searchIndex, hasFavoritesButton } } = useContext(GlobalContext);

    const {
        data,
        loading: isLoading
    } = useGql<
        NewBuildingConstructionQueryVariables,
        NewBuildingConstructionQueryResponse
    >(
        query,
        {
            id: Number(params.id),
            whiteLabel: searchIndex,
            skipFavorites: ! hasFavoritesButton
        }
    );

    const { newBuilding } = data!;
    const regionId = newBuilding.narrowRegionId;

    useEffect(() => {
        document.body.style.overflowX = 'auto';
    }, []);

    const getUrl = useClImageLazySrcSetUrl({ baseUrl: newBuilding.gallery?.baseUrl });

    return (
        <Page
            className={cn()}
            contentClassName={cn('content')}
            regionId={regionId}
        >
            <GalleryContextProvider>
                <ErrorBoundary>
                    <Suspense fallback=''>
                        <NewBuildingConstructionWrapCatcher
                            newBuilding={newBuilding}
                            isLoading={isLoading}
                            cardURL={onChangeGlobalContext.linkBuilder.card(
                                params as unknown as INewBuildingCardType
                            )}
                        />
                    </Suspense>
                </ErrorBoundary>
                <ImageLazySrcSetProvide getUrl={getUrl}>
                    <FSGallery />
                </ImageLazySrcSetProvide>
            </GalleryContextProvider>
        </Page>
    );
};

NewBuildingConstructionPage.displayName = 'NewBuildingConstructionPage';

export default NewBuildingConstructionPage;
